import { create } from "zustand";
import { persist } from "zustand/middleware";
import { TFeedEvent } from "cdt-app-public-api/dist/types";

interface CachedFeedData {
  events: TFeedEvent[];
  lastDocId: string | null;
  hasMore: boolean;
  lastFetch: number;
  eventCode: string;
}

interface FeedState {
  cachedFeeds: Record<string, CachedFeedData>;
  getCachedFeed: (eventCode: string) => CachedFeedData | null;
  setCachedFeed: (
    eventCode: string,
    data: Omit<CachedFeedData, "lastFetch" | "eventCode">
  ) => void;
  appendToFeed: (
    eventCode: string,
    newEvents: TFeedEvent[],
    newLastDocId: string | null,
    hasMore: boolean
  ) => void;
  prependNewEvents: (eventCode: string, newEvents: TFeedEvent[]) => void;
  updateExistingEvents: (
    eventCode: string,
    updatedEvents: TFeedEvent[]
  ) => void;
  addOptimisticPost: (eventCode: string, newPost: TFeedEvent) => void;
  removeEventFromCache: (eventCode: string, eventId: string) => void;
  clearFeedCache: (eventCode?: string) => void;
  isCacheValid: (eventCode: string, maxAgeMs?: number) => boolean;
}

const CACHE_DURATION = 1 * 60 * 60 * 1000; // 1 hour

const shouldAddToCache = (event: TFeedEvent): boolean => {
  if (event.type === "new-post") {
    return Boolean(event.postText || event.postImageStoragePath);
  }
  return true;
};

const mergeEvents = (
  existingEvents: TFeedEvent[],
  newEvents: TFeedEvent[]
): TFeedEvent[] => {
  const eventMap = new Map<string, TFeedEvent>();

  existingEvents.forEach((event) => {
    eventMap.set(event.id, event);
  });

  newEvents.forEach((newEvent) => {
    const existingEvent = eventMap.get(newEvent.id);

    if (!existingEvent) {
      if (shouldAddToCache(newEvent)) {
        eventMap.set(newEvent.id, newEvent);
      }
    } else {
      const existingDate = new Date(
        existingEvent.updatedAt || existingEvent.createdAt
      );
      const newDate = new Date(newEvent.updatedAt || newEvent.createdAt);

      if (newDate >= existingDate) {
        eventMap.set(newEvent.id, newEvent);
      }
    }
  });

  return Array.from(eventMap.values()).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
};

export const useFeedStore = create<FeedState>()(
  persist(
    (set, get) => ({
      cachedFeeds: {},

      getCachedFeed: (eventCode: string) => {
        return get().cachedFeeds[eventCode] || null;
      },

      setCachedFeed: (eventCode: string, data) => {
        const validEvents = data.events.filter(shouldAddToCache);

        set((state) => ({
          cachedFeeds: {
            ...state.cachedFeeds,
            [eventCode]: {
              ...data,
              events: validEvents,
              eventCode,
              lastFetch: Date.now(),
            },
          },
        }));
      },

      appendToFeed: (
        eventCode: string,
        newEvents: TFeedEvent[],
        newLastDocId: string | null,
        hasMore: boolean
      ) => {
        const validEvents = newEvents.filter(shouldAddToCache);

        set((state) => {
          const existing = state.cachedFeeds[eventCode];
          if (!existing) return state;

          const mergedEvents = mergeEvents(existing.events, validEvents);

          return {
            cachedFeeds: {
              ...state.cachedFeeds,
              [eventCode]: {
                ...existing,
                events: mergedEvents,
                lastDocId: newLastDocId,
                hasMore,
                lastFetch: Date.now(),
              },
            },
          };
        });
      },

      prependNewEvents: (eventCode: string, newEvents: TFeedEvent[]) => {
        const validEvents = newEvents.filter(shouldAddToCache);
        if (validEvents.length === 0) return;

        set((state) => {
          const existing = state.cachedFeeds[eventCode];
          if (!existing) return state;

          const mergedEvents = mergeEvents(existing.events, validEvents);

          return {
            cachedFeeds: {
              ...state.cachedFeeds,
              [eventCode]: {
                ...existing,
                events: mergedEvents,
                lastFetch: Date.now(),
              },
            },
          };
        });
      },

      updateExistingEvents: (
        eventCode: string,
        updatedEvents: TFeedEvent[]
      ) => {
        set((state) => {
          const existing = state.cachedFeeds[eventCode];
          if (!existing) return state;

          const mergedEvents = mergeEvents(existing.events, updatedEvents);

          return {
            cachedFeeds: {
              ...state.cachedFeeds,
              [eventCode]: {
                ...existing,
                events: mergedEvents,
                lastFetch: Date.now(),
              },
            },
          };
        });
      },
      addOptimisticPost: (eventCode: string, newPost: TFeedEvent) => {
        if (!shouldAddToCache(newPost)) {
          return;
        }

        set((state) => {
          const existing = state.cachedFeeds[eventCode];
          if (!existing) {
            return state;
          }

          const postExists = existing.events.some(
            (event) => event.id === newPost.id
          );
          if (postExists) {
            return state;
          }

          const updatedEvents = [newPost, ...existing.events];

          return {
            cachedFeeds: {
              ...state.cachedFeeds,
              [eventCode]: {
                ...existing,
                events: updatedEvents,
                lastFetch: Date.now(),
              },
            },
          };
        });
      },

      removeEventFromCache: (eventCode: string, eventId: string) => {
        set((state) => {
          const existing = state.cachedFeeds[eventCode];
          if (!existing) return state;

          const filteredEvents = existing.events.filter(
            (event) => event.id !== eventId
          );

          return {
            cachedFeeds: {
              ...state.cachedFeeds,
              [eventCode]: {
                ...existing,
                events: filteredEvents,
              },
            },
          };
        });
      },

      clearFeedCache: (eventCode?: string) => {
        set((state) => {
          if (eventCode) {
            const { [eventCode]: _, ...rest } = state.cachedFeeds;

            return {
              cachedFeeds: rest,
            };
          }
          return {
            cachedFeeds: {},
          };
        });
      },

      isCacheValid: (eventCode: string, maxAgeMs = CACHE_DURATION) => {
        const cached = get().cachedFeeds[eventCode];
        if (!cached) return false;

        const now = Date.now();
        return now - cached.lastFetch < maxAgeMs;
      },
    }),
    {
      name: "feed-cache",
    }
  )
);
